/**
 * Partner referral code capture.
 *
 * A partner sends traffic to `?ref=THEIRCODE`. That code decides who gets paid
 * commission on this customer, possibly for years — see
 * docs/decisions/partner-programme.md.
 *
 * The gap this closes: the marketing CTA is "Start free today", and the free
 * tier is genuinely free (10 jobs/month). So the real path is
 *
 *     partner link -> free signup -> hit the cap weeks later -> upgrade
 *
 * The code arrives at step one and is needed at step two, which may be the same
 * session or may be a different day. It is therefore persisted here and read
 * back by OnboardingCheck when the org is provisioned. Attribution is recorded
 * once, at signup, and never recomputed — Stripe drops a spent promotion code
 * off the subscription after three months, so there is no later chance to
 * recover it.
 */

const STORAGE_KEY = 'fd_partner_ref';

/** Terms §5: 90 days from first touch. */
export const REFERRAL_TTL_DAYS = 90;
const TTL_MS = REFERRAL_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Codes are upper-case alphanumerics plus dash/underscore, 3–32 chars — the
 * same shape the `partners.code` CHECK constraint enforces.
 *
 * Validating on the way IN matters more than it looks: this value comes
 * straight off a URL any stranger can craft, and it ends up as an argument to
 * a SECURITY DEFINER RPC. Postgres parameter binding already makes that safe
 * from injection, but there is no reason to forward arbitrary attacker-chosen
 * strings into the database, or to fill users' localStorage with them.
 */
const CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

export function normaliseRefCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return CODE_PATTERN.test(code) ? code : null;
}

interface StoredRef {
  code: string;
  /** First touch, epoch ms. */
  ts: number;
}

/**
 * Read `?ref=` from a query string and persist it.
 *
 * First touch wins: an existing, unexpired code is NOT overwritten. If someone
 * arrives via one partner and later clicks another's link before signing up,
 * the partner who actually introduced them keeps the credit. Same rule the
 * `partner_referrals.org_id` unique constraint enforces server-side.
 *
 * Returns the code now in effect, or null.
 */
export function captureRefCode(search: string, now: number = Date.now()): string | null {
  let incoming: string | null = null;
  try {
    incoming = normaliseRefCode(new URLSearchParams(search).get('ref'));
  } catch {
    incoming = null;
  }

  const existing = readRefCode(now);
  if (existing) return existing;

  if (incoming) {
    try {
      const payload: StoredRef = { code: incoming, ts: now };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Private mode, quota, or storage disabled. The code is still returned so
      // a same-session signup is attributed; only the cross-session case is lost.
    }
    return incoming;
  }

  return null;
}

/** The stored code, or null if absent, malformed or past its 90 days. */
export function readRefCode(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredRef>;
    const code = normaliseRefCode(parsed?.code);
    if (!code || typeof parsed.ts !== 'number') {
      clearRefCode();
      return null;
    }
    if (now - parsed.ts > TTL_MS) {
      clearRefCode();
      return null;
    }
    return code;
  } catch {
    clearRefCode();
    return null;
  }
}

export function clearRefCode(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a stale code costs a wrong attribution at worst, and the
    // server-side unique constraint stops it double-crediting anyone.
  }
}
